import { useEffect, useState } from 'react'
import { api } from '@/api/client'
import { Gamepad2, Eye, MousePointer, Image } from 'lucide-react'

interface Stats {
  totalGames: number
  totalViews: number
  totalClicks: number
  totalImages: number
}

interface RecentGame {
  id: string
  name: string
  title: string
  updatedAt: string
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    totalGames: 0,
    totalViews: 0,
    totalClicks: 0,
    totalImages: 0
  })
  const [recentGames, setRecentGames] = useState<RecentGame[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      // 获取游戏列表
      const gamesRes = await api.get('/api/games?limit=100')
      const games = gamesRes.data.data

      // 计算统计
      const totalViews = games.reduce((sum: number, g: any) => sum + (g.viewCount || 0), 0)
      const totalClicks = games.reduce((sum: number, g: any) => sum + (g.clickCount || 0), 0)
      let totalImages = 0
      games.forEach((g: any) => {
        totalImages += 1 // cover
        totalImages += g.images?.screenshots?.length || 0
      })

      setStats({
        totalGames: games.length,
        totalViews,
        totalClicks,
        totalImages
      })

      // 最近更新的游戏
      const sorted = [...games]
        .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 5)
      setRecentGames(sorted)
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (time: string) => {
    const date = new Date(time)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    if (hours < 1) return '刚刚'
    if (hours < 24) return `${hours}小时前`
    const days = Math.floor(hours / 24)
    return `${days}天前`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold">仪表盘</h1>
        <p className="text-muted-foreground">概览数据与最近更新</p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Gamepad2}
          label="游戏总数"
          value={stats.totalGames}
        />
        <StatCard
          icon={Eye}
          label="总浏览量"
          value={stats.totalViews}
        />
        <StatCard
          icon={MousePointer}
          label="总点击量"
          value={stats.totalClicks}
        />
        <StatCard
          icon={Image}
          label="图片总数"
          value={stats.totalImages}
        />
      </div>

      {/* 最近更新 */}
      <div className="rounded-lg border border-border bg-card">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold">最近更新</h2>
        </div>
        <div className="divide-y divide-border">
          {recentGames.map((game) => (
            <div
              key={game.id}
              className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
            >
              <div>
                <p className="font-medium">{game.title}</p>
                <p className="text-sm text-muted-foreground">{game.name}</p>
              </div>
              <span className="text-sm text-muted-foreground">
                {formatTime(game.updatedAt)}
              </span>
            </div>
          ))}
          {recentGames.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              暂无数据
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  icon: React.ElementType
  label: string
  value: number
}

function StatCard({ icon: Icon, label, value }: StatCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  )
}
